import { Point, IsoPoint, Line } from '@/app/types/cad';
import { isoToScreen, screenToIso, distance } from './isometric';

/**
 * Calculates the points for a tangent arc (fillet) between two lines.
 * This function does NOT modify the input lines.
 * 
 * @param line1 First line
 * @param line2 Second line
 * @param radius Desired radius in World Units (mm)
 * @param gridSize Grid size for coordinate conversion
 * @param clickPoint1 Click position on line 1 (to determine side)
 * @param clickPoint2 Click position on line 2 (to determine side)
 * @returns Object with arc parameters or null
 */
export function calculateTangentArc(
  line1: Line,
  line2: Line,
  radius: number,
  gridSize: number,
  clickPoint1?: Point,
  clickPoint2?: Point
): { 
  center: IsoPoint, 
  radius: number, 
  startAngle: number, 
  endAngle: number, 
  counterClockwise: boolean,
  tangentStart: IsoPoint,
  tangentEnd: IsoPoint,
  intersection: IsoPoint
} | null {
  const toWorld = (p: IsoPoint) => isoToScreen(p, gridSize);
  const toIso = (p: Point) => screenToIso(p, gridSize);

  // Convert lines to World Space
  const l1s = toWorld(line1.start);
  const l1e = toWorld(line1.end);
  const l2s = toWorld(line2.start);
  const l2e = toWorld(line2.end);

  // Use radius directly as World Units (pixels) to ensure visibility
  const worldRadius = radius;

  // Direction vectors
  const v1 = { x: l1e.x - l1s.x, y: l1e.y - l1s.y };
  const v2 = { x: l2e.x - l2s.x, y: l2e.y - l2s.y };
  
  const len1 = Math.sqrt(v1.x * v1.x + v1.y * v1.y);
  const len2 = Math.sqrt(v2.x * v2.x + v2.y * v2.y);
  
  if (len1 === 0 || len2 === 0) return null;

  const n1 = { x: v1.x / len1, y: v1.y / len1 };
  const n2 = { x: v2.x / len2, y: v2.y / len2 };

  // Check parallelism
  const dot = n1.x * n2.x + n1.y * n2.y;
  const isParallel = Math.abs(dot) > 0.999;

  if (isParallel) {
    // Parallel Lines (U-Turn)
    let p1Connect = l1e; 
    if (clickPoint1) {
       p1Connect = projectPointOnLine(clickPoint1, l1s, n1);
    }
    const p2Connect = projectPointOnLine(p1Connect, l2s, n2);
    
    // Actual Radius
    const dist = distance(p1Connect, p2Connect);
    if (dist < 0.1) return null;
    const actualWorldRadius = dist / 2;
    
    // Center
    const center = {
      x: (p1Connect.x + p2Connect.x) / 2,
      y: (p1Connect.y + p2Connect.y) / 2
    };

    const rx = p1Connect.x - center.x;
    const ry = p1Connect.y - center.y;
    const t = { x: -ry, y: rx }; // CCW tangent
    
    const dotT = t.x * n1.x + t.y * n1.y;
    const isCCW = dotT > 0;
    
    const startAngle = Math.atan2(p1Connect.y - center.y, p1Connect.x - center.x);
    const sweep = isCCW ? Math.PI : -Math.PI;
    const endAngle = startAngle + sweep;
    
    // For parallel lines, there is no single intersection point, but we can approximate it or omit
    // For trimming purposes, the "intersection" is effectively infinite or irrelevant, 
    // but the tangent points are exactly p1Connect and p2Connect.
    
    return {
        center: toIso(center),
        radius: actualWorldRadius,
        startAngle,
        endAngle,
        counterClockwise: isCCW,
        tangentStart: toIso(p1Connect),
        tangentEnd: toIso(p2Connect),
        intersection: toIso(center) // Dummy value, not a real corner
    };

  } else {
    // Intersecting Lines (Corner)
    const intersection = getLineIntersection(l1s, l1e, l2s, l2e);
    if (!intersection) return null; 
    
    // Determine Ray directions based on Line geometry, not just click points
    // This ensures the arc is perfectly tangent to the lines, even if click was slightly off
    const getRay = (start: Point, end: Point, click: Point | undefined, intersect: Point) => {
        // Line vector
        const dx = end.x - start.x;
        const dy = end.y - start.y;
        const len = Math.sqrt(dx*dx + dy*dy);
        if (len === 0) return { x: 0, y: 0 };
        const dir = { x: dx/len, y: dy/len };
        
        // If no click, default to direction from intersection to end
        if (!click) {
             return dir;
        }

        // Check which direction from intersection the click point is
        const vClick = { x: click.x - intersect.x, y: click.y - intersect.y };
        const dot = vClick.x * dir.x + vClick.y * dir.y;
        
        // Return the line direction that points towards the click
        return dot >= 0 ? dir : { x: -dir.x, y: -dir.y };
    };

    const ray1 = getRay(l1s, l1e, clickPoint1, intersection);
    const ray2 = getRay(l2s, l2e, clickPoint2, intersection);
    
    const angle1 = Math.atan2(ray1.y, ray1.x);
    const angle2 = Math.atan2(ray2.y, ray2.x);
    
    // Bisector
    let diff = angle2 - angle1;
    while (diff <= -Math.PI) diff += 2*Math.PI;
    while (diff > Math.PI) diff -= 2*Math.PI;
    
    const halfAngle = diff / 2;
    const bisectorAngle = angle1 + halfAngle;
    
    // Distance from intersection to center
    const distToCenter = worldRadius / Math.sin(Math.abs(halfAngle));
    
    const center = {
      x: intersection.x + Math.cos(bisectorAngle) * distToCenter,
      y: intersection.y + Math.sin(bisectorAngle) * distToCenter
    };
    
    // Tangent points
    const t1 = projectPointOnLine(center, intersection, ray1);
    const t2 = projectPointOnLine(center, intersection, ray2);
    
    // Generate Arc
    const startA = Math.atan2(t1.y - center.y, t1.x - center.x);
    const endA = Math.atan2(t2.y - center.y, t2.x - center.x);
    
    // Sweep
    let dAng = endA - startA;
    while (dAng <= -Math.PI) dAng += 2*Math.PI;
    while (dAng > Math.PI) dAng -= 2*Math.PI;
    
    return {
        center: toIso(center),
        radius: worldRadius,
        startAngle: startA,
        endAngle: endA,
        counterClockwise: dAng < 0,
        tangentStart: toIso(t1),
        tangentEnd: toIso(t2),
        intersection: toIso(intersection)
    };
  }
}

// Helpers
function getLineIntersection(p1: Point, p2: Point, p3: Point, p4: Point): Point | null {
  const denom = (p4.y - p3.y) * (p2.x - p1.x) - (p4.x - p3.x) * (p2.y - p1.y);
  if (denom === 0) return null;
  const ua = ((p4.x - p3.x) * (p1.y - p3.y) - (p4.y - p3.y) * (p1.x - p3.x)) / denom;
  return {
    x: p1.x + ua * (p2.x - p1.x),
    y: p1.y + ua * (p2.y - p1.y)
  };
}

function normalize(v: Point): Point {
  const len = Math.sqrt(v.x * v.x + v.y * v.y);
  if (len === 0) return { x: 0, y: 0 };
  return { x: v.x / len, y: v.y / len };
}

function projectPointOnLine(p: Point, lineStart: Point, lineDirNormalized: Point): Point {
  const v = { x: p.x - lineStart.x, y: p.y - lineStart.y };
  const d = v.x * lineDirNormalized.x + v.y * lineDirNormalized.y;
  return {
    x: lineStart.x + lineDirNormalized.x * d,
    y: lineStart.y + lineDirNormalized.y * d
  };
}
